import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import FinancialAccountService from '../../services/FinancialAccountService';

export default function FinancialAccountDetailPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Date Filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Manual Transaction States
  const [showTxDialog, setShowTxDialog] = useState(false);
  const [txType, setTxType] = useState("INBOUND");
  const [txAmount, setTxAmount] = useState(null);
  const [txDescription, setTxDescription] = useState("");
  const [txDate, setTxDate] = useState(new Date());
  const [submittingTx, setSubmittingTx] = useState(false);

  const handleOpenTransactionDialog = (type) => {
    setTxType(type);
    setTxAmount(null);
    setTxDescription("");
    setTxDate(new Date());
    setShowTxDialog(true);
  };

  const handleSubmitTransaction = async () => {
    if (!txAmount || txAmount <= 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Tutar 0'dan büyük olmalıdır"
      });
      return;
    }

    try {
      setSubmittingTx(true);
      const res = await FinancialAccountService.addTransaction({
        financialAccountId: Number(id),
        amount: txAmount,
        transactionType: txType,
        description: txDescription,
        transactionDate: txDate
      });

      if (res.success) {
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: res.message || "İşlem kaydedildi"
        });
        setShowTxDialog(false);
        await loadData();
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Hata",
          detail: res.message
        });
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "İşlem sırasında bir hata oluştu"
      });
    } finally {
      setSubmittingTx(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await FinancialAccountService.getDetail(id);
      if (res.success) {
        setData(res.data);
      } else {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message });
      }
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Hesap detayları yüklenemedi' });
    } finally {
      setLoading(false);
    }
  };

  const getAccountTypeLabel = (type) => {
    switch (type) {
      case 1: return 'Kasa Hesabı';
      case 2: return 'Banka Hesabı';
      case 3: return 'Firma Kredi Kartı';
      case 4: return 'POS Hesabı';
      case 5: return 'Sanal POS';
      default: return 'Diğer';
    }
  };

  const getAccountColors = (type) => {
    switch (type) {
      case 1: // Kasa
        return {
          icon: "pi pi-money-bill",
          badge: "!bg-emerald-50 !text-emerald-700 !border !border-emerald-200 dark:!bg-emerald-950/30 dark:!text-emerald-400 dark:!border-emerald-900/50",
          iconColor: "!text-emerald-600 !bg-emerald-50 !border !border-emerald-200 dark:!text-emerald-400 dark:!bg-emerald-950/30 dark:!border-emerald-900/50",
          accent: "emerald-500",
        };
      case 2: // Banka
        return {
          icon: "pi pi-building",
          badge: "!bg-blue-50 !text-blue-700 !border !border-blue-200 dark:!bg-blue-950/30 dark:!text-blue-400 dark:!border-blue-900/50",
          iconColor: "!text-blue-600 !bg-blue-50 !border !border-blue-200 dark:!text-blue-400 dark:!bg-blue-950/30 dark:!border-blue-900/50",
          accent: "blue-500",
        };
      case 3: // Firma Kartı
        return {
          icon: "pi pi-credit-card",
          badge: "!bg-orange-50 !text-orange-700 !border !border-orange-200 dark:!bg-orange-950/30 dark:!text-orange-400 dark:!border-orange-900/50",
          iconColor: "!text-orange-600 !bg-orange-50 !border !border-orange-200 dark:!text-orange-400 dark:!bg-orange-950/30 dark:!border-orange-900/50",
          accent: "orange-500",
        };
      case 4: // POS Cihazı
        return {
          icon: "pi pi-print",
          badge: "!bg-cyan-50 !text-cyan-700 !border !border-cyan-200 dark:!bg-cyan-950/30 dark:!text-cyan-400 dark:!border-cyan-900/50",
          iconColor: "!text-cyan-600 !bg-cyan-50 !border !border-cyan-200 dark:!text-cyan-400 dark:!bg-cyan-950/30 dark:!border-cyan-900/50",
          accent: "cyan-500",
        };
      case 5: // Sanal POS
        return {
          icon: "pi pi-globe",
          badge: "!bg-purple-50 !text-purple-700 !border !border-purple-200 dark:!bg-purple-950/30 dark:!text-purple-400 dark:!border-purple-900/50",
          iconColor: "!text-purple-600 !bg-purple-50 !border !border-purple-200 dark:!text-purple-400 dark:!bg-purple-950/30 dark:!border-purple-900/50",
          accent: "purple-500",
        };
      default:
        return {
          icon: "pi pi-wallet",
          badge: "!bg-slate-50 !text-slate-700 !border !border-slate-200 dark:!bg-slate-800/40 dark:!text-slate-400 dark:!border-slate-700/50",
          iconColor: "!text-slate-600 !bg-slate-50 !border !border-slate-200 dark:!text-slate-400 dark:!bg-slate-800/40 dark:!border-slate-700/50",
          accent: "slate-500",
        };
    }
  };

  const amountBodyTemplate = (rowData) => {
    const isNegative = rowData.transactionType === 'OUTBOUND';
    return (
      <span className={`font-black ${isNegative ? 'text-red-600 dark:text-red-400' : 'text-emerald-650 dark:text-emerald-400'}`}>
        {isNegative ? '-' : '+'}{rowData.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
      </span>
    );
  };

  const dateBodyTemplate = (rowData) => {
    return (
      <div className="flex flex-col">
        <span className="font-bold text-slate-850 dark:text-slate-200 text-xs">{new Date(rowData.transactionDate).toLocaleDateString('tr-TR')}</span>
        <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider mt-0.5">{new Date(rowData.transactionDate).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
    );
  };

  const referenceTemplate = (rowData) => {
    if (!rowData.referenceType) return <span className="text-slate-400">-</span>;
    
    const translateReference = (type) => {
      const upperType = type.toUpperCase();
      switch (upperType) {
        case "SALE": return "SATIŞ";
        case "PAYMENT": return "ÖDEME";
        case "COLLECTION": return "TAHSİLAT";
        case "MANUAL": return "MANUEL";
        case "TRANSFER": return "TRANSFER";
        case "OPENING_BALANCE": return "AÇILIŞ BAKİYESİ";
        case "EXPENSE": return "GİDER";
        case "CHEQUE": return "ÇEK";
        case "PROMISSORY_NOTE": return "SENET";
        case "EMPLOYEE_PAYMENT": return "PERSONEL ÖDEMESİ";
        case "PURCHASE": return "ALIŞ";
        default: return type;
      }
    };

    return (
      <Tag
        value={translateReference(rowData.referenceType)}
        className="text-[9px] font-black uppercase px-2 py-0.5 rounded-lg bg-slate-50 dark:bg-slate-950/30 text-slate-600 dark:text-slate-400 border border-slate-250 dark:border-slate-800"
      />
    );
  };

  const filteredTransactions = useMemo(() => {
    if (!data?.transactions) return [];
    return data.transactions.filter(t => {
      const tDate = new Date(t.transactionDate);
      if (startDate) {
        const sDate = new Date(startDate);
        sDate.setHours(0, 0, 0, 0);
        if (tDate < sDate) return false;
      }
      if (endDate) {
        const eDate = new Date(endDate);
        eDate.setHours(23, 59, 59, 999);
        if (tDate > eDate) return false;
      }
      return true;
    });
  }, [data?.transactions, startDate, endDate]);

  const inboundTransactions = useMemo(() => {
    return filteredTransactions.filter(t => t.transactionType === 'INBOUND');
  }, [filteredTransactions]);

  const outboundTransactions = useMemo(() => {
    return filteredTransactions.filter(t => t.transactionType === 'OUTBOUND');
  }, [filteredTransactions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 bg-slate-100/30 dark:bg-slate-950 min-h-screen">
        <i className="pi pi-spin pi-spinner text-4xl text-slate-350 dark:text-slate-700" />
      </div>
    );
  }

  return (
    <div className="relative p-2 md:p-4 space-y-4 animate-fade-in text-slate-700 min-h-screen bg-slate-100/50 dark:bg-slate-950">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

      {/* Page Header */}
      <div className="relative overflow-hidden rounded-[2rem] border border-white/70 dark:border-slate-800/80 bg-slate-950 shadow-2xl shadow-slate-900/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(34,197,94,0.32),transparent_24%),radial-gradient(circle_at_78%_18%,rgba(59,130,246,0.34),transparent_28%),radial-gradient(circle_at_56%_88%,rgba(168,85,247,0.28),transparent_28%)]" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/92 to-slate-950/95" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-5 p-4 md:p-6">
          <div className="flex items-start gap-4 min-w-0">
            <button
              onClick={() => navigate('/finance/accounts')}
              className="flex items-center justify-center !w-11 !h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors border-none cursor-pointer shrink-0"
            >
              <i className="pi pi-arrow-left text-lg" />
            </button>
            <div className="min-w-0">
              <span className={`inline-flex items-center gap-2 text-[10px] font-black tracking-[0.25em] uppercase !text-cyan-200 bg-white/10 border border-white/10 px-3 py-1 rounded-full mb-3`}>
                <i className="pi pi-sparkles text-[12px]" /> {getAccountTypeLabel(data?.account.accountType)}
              </span>
              <h1 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight truncate">
                {data?.account.name}
              </h1>
              <p className="text-sm text-slate-300 font-semibold m-0 mt-2 truncate">
                {data?.account.accountNumber || 'HESAP NO BELİRTİLMEMİŞ'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              label="Giriş Ekle"
              icon="pi pi-plus"
              className="!bg-emerald-600 border-none hover:!bg-emerald-700 rounded-2xl font-black text-white px-4 py-3 shadow-md shadow-emerald-600/10 text-xs"
              onClick={() => handleOpenTransactionDialog("INBOUND")}
            />
            <Button
              label="Çıkış Ekle"
              icon="pi pi-minus"
              className="!bg-red-600 border-none hover:!bg-red-700 rounded-2xl font-black text-white px-4 py-3 shadow-md shadow-red-600/10 text-xs"
              onClick={() => handleOpenTransactionDialog("OUTBOUND")}
            />
            <Button
              label="Düzenle"
              icon="pi pi-pencil"
              className="!bg-white/10 hover:!bg-white/15 !text-white !border !border-white/15 font-black text-xs px-4 py-3 rounded-2xl shadow-sm backdrop-blur"
              onClick={() => navigate(`/finance/accounts/edit/${id}`)}
            />
          </div>
        </div>
      </div>

      {/* Date Filter Control Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 rounded-3xl border border-white/85 dark:border-slate-800/80 bg-white/80 dark:bg-slate-900/70 backdrop-blur p-4 shadow-lg shadow-slate-200/50 dark:shadow-black/20">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
            <i className="pi pi-filter text-xs"></i>
          </div>
          <div>
            <h3 className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider m-0">Tarih Filtresi</h3>
            <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider m-0">Hareketleri tarihe göre listeleyin</p>
          </div>
        </div>

        <div className="flex flex-row items-center gap-2 w-full md:w-auto">
          <Calendar
            value={startDate}
            onChange={(e) => setStartDate(e.value)}
            placeholder="Başlangıç"
            dateFormat="dd.mm.yy"
            showIcon
            className="flex-1 md:w-40 text-xs font-bold"
            inputClassName="py-2 px-3 text-xs font-bold rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40 h-[38px] w-full"
          />
          <Calendar
            value={endDate}
            onChange={(e) => setEndDate(e.value)}
            placeholder="Bitiş"
            dateFormat="dd.mm.yy"
            showIcon
            className="flex-1 md:w-40 text-xs font-bold"
            inputClassName="py-2 px-3 text-xs font-bold rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40 h-[38px] w-full"
          />
          {(startDate || endDate) && (
            <Button
              icon="pi pi-trash"
              label="Temizle"
              className="p-button-text p-button-danger font-black text-xs px-3 h-[38px] rounded-xl border-none"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
              }}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Summary Card */}
        <div className={`lg:col-span-1 relative overflow-hidden rounded-[2rem] border border-white/80 dark:border-slate-800/80 bg-white dark:bg-slate-900 p-6 shadow-xl shadow-slate-200/60 dark:shadow-black/20 flex flex-col justify-between`}>
          <div className="space-y-6">
            <div>
              <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] block mb-1">TOPLAM BAKİYE</span>
              <div className="flex items-baseline gap-2">
                <span className={`text-4xl font-black ${data?.account.balance >= 0 ? 'text-slate-800 dark:text-slate-100' : 'text-red-600 dark:text-red-400'}`}>
                  {data?.account.balance?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </span>
                <span className="text-lg font-bold text-slate-400">{data?.account.currencyCode || "TRY"}</span>
              </div>
            </div>

            <div className="space-y-4 pt-6 border-t border-slate-100 dark:border-slate-800/60">
              <div className="flex justify-between items-center text-xs">
                <span className="font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Açılış Bakiyesi</span>
                <span className="font-black text-slate-700 dark:text-slate-300">
                  {data?.account.openingBalance?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Hesap Tipi</span>
                <span className="font-black text-slate-700 dark:text-slate-300">{getAccountTypeLabel(data?.account.accountType)}</span>
              </div>

              {data?.account.accountType === 4 && (
                <>
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Komisyon Oranı</span>
                    <span className="font-black text-slate-700 dark:text-slate-300">%{data?.account.defaultCommissionRate}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Valör Süresi</span>
                    <span className="font-black text-slate-700 dark:text-slate-300">{data?.account.defaultValueDays} Gün</span>
                  </div>
                  {data?.account.merchantNo && (
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Üye İşyeri No</span>
                      <span className="font-black text-slate-700 dark:text-slate-300">{data?.account.merchantNo}</span>
                    </div>
                  )}
                  {data?.account.terminalNo && (
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Terminal No</span>
                      <span className="font-black text-slate-700 dark:text-slate-300">{data?.account.terminalNo}</span>
                    </div>
                  )}
                </>
              )}

              {data?.account.accountType === 3 && (
                <>
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Limit</span>
                    <span className="font-black text-slate-700 dark:text-slate-300">{data?.account.cardLimit?.toLocaleString()} {data?.account.currencyCode}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Kesim Günü</span>
                    <span className="font-black text-slate-700 dark:text-slate-300">Ayın {data?.account.statementDay}. günü</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Son Ödeme</span>
                    <span className="font-black text-slate-700 dark:text-slate-300">Ayın {data?.account.paymentDueDay}. günü</span>
                  </div>
                </>
              )}

              <div className="flex justify-between items-center text-xs">
                <span className="font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Durum</span>
                <span className={`text-[9px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                  data?.account.isActive 
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-250 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/50" 
                    : "bg-red-50 text-red-700 border border-red-250 dark:bg-red-950/30 dark:text-red-400 dark:border-red-900/50"
                }`}>
                  {data?.account.isActive ? 'AKTİF' : 'PASİF'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Transactions Tabs / Tables */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white/90 dark:bg-slate-900/85 backdrop-blur border border-white/80 dark:border-slate-800 rounded-3xl overflow-hidden shadow-xl shadow-slate-200/60 dark:shadow-black/20">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800/60 flex items-center justify-between">
              <h2 className="text-xs font-black text-slate-800 dark:text-slate-200 m-0 uppercase tracking-widest">GİRİŞ HAREKETLERİ (TAHSİLATLAR)</h2>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center border border-emerald-100/30">
                <i className="pi pi-arrow-down-left text-xs"></i>
              </div>
            </div>
            <DataTable
              value={inboundTransactions}
              paginator
              rows={5}
              className="p-datatable-sm"
              rowClassName={() => "hover:bg-indigo-50/40 dark:hover:bg-slate-800/70 transition-colors border-b border-slate-50 dark:border-slate-800/50"}
              emptyMessage="Giriş hareketi bulunamadı"
            >
              <Column header="TARİH" body={dateBodyTemplate} className="w-32" />
              <Column field="description" header="AÇIKLAMA" className="text-sm font-medium" />
              <Column header="REFERANS" body={referenceTemplate} className="w-32" />
              <Column header="TUTAR" body={amountBodyTemplate} className="text-right w-32" />
            </DataTable>
          </div>

          <div className="bg-white/90 dark:bg-slate-900/85 backdrop-blur border border-white/80 dark:border-slate-800 rounded-3xl overflow-hidden shadow-xl shadow-slate-200/60 dark:shadow-black/20">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800/60 flex items-center justify-between">
              <h2 className="text-xs font-black text-slate-800 dark:text-slate-200 m-0 uppercase tracking-widest">ÇIKIŞ HAREKETLERİ (ÖDEMELER)</h2>
              <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 flex items-center justify-center border border-red-100/30">
                <i className="pi pi-arrow-up-right text-xs"></i>
              </div>
            </div>
            <DataTable
              value={outboundTransactions}
              paginator
              rows={5}
              className="p-datatable-sm"
              rowClassName={() => "hover:bg-indigo-50/40 dark:hover:bg-slate-800/70 transition-colors border-b border-slate-50 dark:border-slate-800/50"}
              emptyMessage="Çıkış hareketi bulunamadı"
            >
              <Column header="TARİH" body={dateBodyTemplate} className="w-32" />
              <Column field="description" header="AÇIKLAMA" className="text-sm font-medium" />
              <Column header="REFERANS" body={referenceTemplate} className="w-32" />
              <Column header="TUTAR" body={amountBodyTemplate} className="text-right w-32" />
            </DataTable>
          </div>
        </div>
      </div>

      {/* Manuel İşlem Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${txType === "INBOUND" ? "bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600" : "bg-red-100 dark:bg-red-950/50 text-red-600"}`}>
              <i className={`pi ${txType === "INBOUND" ? "pi-arrow-down-left" : "pi-arrow-up-right"} text-xl`}></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 dark:text-slate-100 m-0">
                {txType === "INBOUND" ? "Bakiye Girişi Ekle" : "Bakiye Çıkışı Ekle"}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium m-0">
                Hesaba ait manuel işlem hareketini tanımlayın
              </p>
            </div>
          </div>
        }
        visible={showTxDialog}
        style={{ width: '400px' }}
        onHide={() => setShowTxDialog(false)}
        className="p-fluid"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-secondary font-bold" onClick={() => setShowTxDialog(false)} />
            <Button
              label="Onayla"
              icon="pi pi-check"
              className={`border-none font-black text-white ${txType === "INBOUND" ? "!bg-emerald-600 hover:!bg-emerald-700" : "!bg-red-600 hover:!bg-red-700"}`}
              onClick={handleSubmitTransaction}
              loading={submittingTx}
              disabled={!txAmount || txAmount <= 0}
            />
          </div>
        )}
      >
        <div className="space-y-4 pt-4">
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">TUTAR</label>
            <InputNumber
              value={txAmount}
              onValueChange={(e) => setTxAmount(e.value)}
              min={0}
              placeholder="0.00"
              autoFocus
              className="w-full"
              inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-500"
              mode="currency"
              currency="TRY"
              locale="tr-TR"
            />
          </div>

          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">İŞLEM TARİHİ</label>
            <Calendar
              value={txDate}
              onChange={(e) => setTxDate(e.value)}
              dateFormat="dd.mm.yy"
              showTime
              hourFormat="24"
              showIcon
              appendTo={mounted ? document.body : "self"}
              className="w-full"
              inputClassName="font-bold py-3 rounded-xl border-slate-200"
            />
          </div>

          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">AÇIKLAMA</label>
            <InputText
              value={txDescription}
              onChange={(e) => setTxDescription(e.target.value)}
              placeholder="İşlem açıklaması girin..."
              className="rounded-xl border-slate-200 py-3 font-medium text-sm"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
